import { CurrencyPipe } from '@angular/common';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators
} from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, throwError } from 'rxjs';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { DataTableHeaderOption, ErrorControl, RadioSelectComponent, createErrorControlMap } from '@dxo/ng-swift-ui';
import { AccountType, AccountTypeUI } from '@benefitsui/app/api/benefits-enums';
import {
  AmountDetails,
  ApiResponseOfPolicySearchResultsResponse,
  BillingGroupInfo,
  GroupInformation,
  InvoiceInfo,
  LineItemInfo,
  PaymentClient,
  PaymentInformation,
  PaymentRequest,
  PolicyClient,
  SearchCategory,
  SearchSortDirection,
  SearchSortField,
  UserInformation
} from '@benefitsui/app/api/benefits-clients';
import { BillingService } from '@benefitsui/app/pages/billing-page/billing.service';
import { Constants, PaymentData, PaymentSearchParameters } from '@benefitsui/app/pages/billing-page/billing-page.types';
import { BillingPageComponent } from '@benefitsui/app/pages/billing-page/billing-page.component';
import { UserContextService } from '@benefitsui/app/services/user-context/user-context.service';
import { UserContext } from '@benefitsui/app/services/user-context/user-context.types';
import { ModalData, ModalType } from './payment.types';
import { PolicyContext } from '@benefitsui/app/services/policy-context/policy-context.types';
import { PolicyContextService } from '@benefitsui/app/services/policy-context/policy-context.service';
import { HttpErrorService } from '@benefitsui/app/services/http-error/http-error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';

// :  Interfaces
interface IPaymentChange {
  eventString: string;
  item: PaymentData;
}

// :  Constants
const AccountFieldLengthMax = 17;
const AccountFieldLengthMin = 5;
const RoutingFieldLength = 9;

// :  Computed constants
const ErrorMessageAccount = `Entry must be ${AccountFieldLengthMin} to ${AccountFieldLengthMax} digits`;
const ErrorMessageRouting = `Entry must be ${RoutingFieldLength} digits.`;

const getErrorMap = (formGroup: UntypedFormGroup) =>
  createErrorControlMap(formGroup, {
    accountNumber: {
      required: ErrorMessageAccount,
      maxlength: `Entry must not exceed ${AccountFieldLengthMax} characters`,
      minlength: `Entry must be at least ${AccountFieldLengthMin} characters`
    },
    routingNumber: {
      required: ErrorMessageRouting,
      maxlength: ErrorMessageRouting,
      minlength: ErrorMessageRouting
    },
    accountTypeSelection: {
      required: 'Please select account'
    }
  }) as object as {
    accountNumber: ErrorControl;
    routingNumber: ErrorControl;
    accountTypeSelection: ErrorControl;
  };

@Component({
  selector: 'app-billing-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  providers: [PolicyContextService]
})
export class BillingPaymentComponent implements OnInit {
  // :  Constants
  AccountKey = 'account';
  AccountNumberKey = 'accountNumber';
  AccountNumberText = 'Account number';
  AccountTypeName = 'account-type';
  AccountTypeSelectionKey = 'accountTypeSelection';
  AccountTypeText = 'Account type';
  AmountKey = Constants.AmountText;
  AmountText = Constants.AmountText;
  AmountUiKey = 'AmountUI';
  BillGroupKey = Constants.BillGroupKey;
  BillGroupText = Constants.BillGroupText;
  BillGroupNameKey = 'BillGroupName';
  ButtonTextPayNow = 'Pay now';
  CharSetNumericWithDecimal = /[0-9.]/;
  CharSetNumericWithDecimalDollarComma = /[0-9.$,]/;
  CloseText = Constants.CloseText;
  ErrorMessageAccountLocal = ErrorMessageAccount;
  ErrorMessageCurrencyNotFloat = 'Must be a valid amount';
  ErrorMessageRoutingLocal = ErrorMessageRouting;
  InvoicePeriodAndNumberText = Constants.InvoicePeriodAndNumberText;
  InvoiceKey = Constants.InvoiceText;
  InvoiceText = Constants.InvoiceText;
  LegalType = ModalType.Legal;
  ModalBodyEmail = 'An email confirmation is on the way.';
  ModalBodyError4xx = 'Please ensure all details are entered correctly and try again.';
  ModalBodyError5xx =
    'Please attempt the transaction again after some time. Rest assured, our IT team has been informed and is looking into it.';
  ModalBodyLegal =
    'I authorize my payment to be processed as an electronic funds transfer or draft drawn from my' +
    ' account. If the payment is returned unpaid, I authorize you or your service provider to collect the payment' +
    " and my state's return item fee by electronic funds transfer(s) or draft(s) drawn from my account. If this" +
    ' payment is from a corporate account, I make these authorizations as an authorized corporate representative' +
    ' and agree that the NACHA operating rules will bind the entity.';
  ModalBodyPermission = 'You are not allowed to pay invoices.';
  ModalBodySuccess = 'Thank you for your payment.  Processing the transaction may take two business days to complete.';
  ModalPrimary = false;
  ModalShowClose = false;
  ModalTitleError4xx = 'We encountered a snag with processing your payment';
  ModalTitleError5xx = 'There is an issue with your payment';
  ModalTitleLegal = 'Electronic check terms & conditions';
  ModalTitlePermission = "Oops, you don't have access";
  ModalTitleSuccess = 'We received your payment';
  ModalWidth = 482;
  PaymentAmountText = 'Payment amount';
  PaymentCardHeader = 'Selected invoice(s) to pay';
  RoutingKey = 'routing';
  RoutingNumberKey = 'routingNumber';
  RoutingNumberText = 'Routing number';
  SortDirectionLocal = SearchSortDirection;
  SortKey = Constants.SortKey;
  TotalAmountText = 'Total amount';

  // :  Computed constants
  LegalPrefaceText = `By clicking "${this.ButtonTextPayNow}", I agree to the `;

  // :  Constructor
  constructor(
    private billingService: BillingService,
    private errorService: HttpErrorService,
    private formBuilder: UntypedFormBuilder,
    private parent: BillingPageComponent,
    private paymentClient: PaymentClient,
    private policyClient: PolicyClient,
    private policyContextService: PolicyContextService,
    private userContextService: UserContextService
  ) {
    this.formGroup = this.formBuilder.group({
      accountNumber: [
        Constants.StringEmpty,
        [
          Validators.required,
          Validators.minLength(AccountFieldLengthMin),
          Validators.maxLength(AccountFieldLengthMax),
          Validators.pattern('[0-9]{5,17}')
        ]
      ],
      routingNumber: [
        Constants.StringEmpty,
        [
          Validators.required,
          Validators.maxLength(RoutingFieldLength),
          Validators.minLength(RoutingFieldLength),
          Validators.pattern('[0-9]{9,9}')
        ]
      ],
      accountTypeSelection: [Constants.StringEmpty, [Validators.required]]
    });

    this.formErrors = {
      ...getErrorMap(this.formGroup)
    };

    this.paymentDataArray = this.billingService.getPaymentTableCells();
    this.policyContext = this.policyContextService.get();
    this.totalAmount = this.totalAmountCalculate(this.paymentDataArray);
    this.userContext = this.userContextService.get();

    // :  Asynchronously retrieve the group number information (tuple array)
    // this.groupTupleArrayRetrieveAsync(this.tupleArray);
  }

  // :  Data members
  private hasSubmitPermission = false;
  private page$ = new BehaviorSubject<number>(1);
  private pageSize$ = new BehaviorSubject<number>(10);
  private paymentDataArray: PaymentData[] = [];
  private policyContext: PolicyContext;
  private sortDirection$ = new BehaviorSubject<SearchSortDirection>(SearchSortDirection.Descending);
  private sortField$ = new BehaviorSubject<SearchSortField>(SearchSortField.Invoice);
  private tupleArray: [employerId: string, groupId: string][] = [];
  private userContext: UserContext;

  accountTypeOptions: RadioSelectComponent['data'] = [
    [AccountType.Checking, AccountTypeUI.Checking],
    [AccountType.Savings, AccountTypeUI.Savings]
  ].map(([value, label]) => ({ name: this.AccountTypeName, value, label, id: value }));

  formErrors: { accountNumber: ErrorControl; routingNumber: ErrorControl; accountTypeSelection: ErrorControl };
  formGroup: UntypedFormGroup;

  hasSubText = false;
  isLoading = false;

  modalData: ModalData = {
    title: Constants.StringEmpty,
    body: Constants.StringEmpty,
    subText: Constants.StringEmpty,
    type: ModalType.Success
  };

  modalShow = false;

  searchParameter: PaymentSearchParameters = {
    page: 1,
    pageSize: 10,
    sortField: SearchSortField.Invoice,
    sortDirection: SearchSortDirection.Descending
  };

  tableHeaders: DataTableHeaderOption[] = [
    { text: this.InvoicePeriodAndNumberText, key: Constants.InvoiceKey, width: '40%' },
    { text: this.BillGroupText, key: Constants.BillGroupKey, width: '45%' },
    { text: this.PaymentAmountText, key: this.AmountUiKey, align: 'right' }
  ];

  totalAmount = 0;
  totalLines = 0;

  // :  Dependent data
  billings$ = combineLatest([
    this.page$.pipe(),
    this.pageSize$.pipe(),
    this.sortDirection$.pipe(),
    this.sortField$.pipe()
  ])
    .pipe(
      switchMap((vals) => {
        this.searchParameter.page = vals[0];
        this.searchParameter.pageSize = vals[1];
        this.searchParameter.sortDirection = vals[2];
        this.searchParameter.sortField = vals[3];

        return this.billingService.getPaymentLines(this.searchParameter);
      })
    )
    .pipe(
      map((x) => x),
      tap(() => (this.totalLines = this.billingService.totalPaymentLines))
    );

  // :  Methods

  // :  Must remain 'public' for unit testing
  createEndpointPaymentRequest(
    formGroup: UntypedFormGroup,
    groupNumber: string,
    paymentDataArray: PaymentData[]
  ): PaymentRequest {
    const message = `createEndpointPaymentRequest:  paymentDataArray = ${JSON.stringify(paymentDataArray)}`;
    console.log(message);

    const amountDetails: AmountDetails = {
      currencyCode: Constants.CurrencyUsdKey,
      totalAmount: this.totalAmount
    };

    const firstName = this.userContext?.currentUserFirstName;
    const lastName = this.userContext?.currentUserLastName;
    const email = this.userContext?.currentUserEmail;
    const phoneNumber = this.userContext?.currentUserPhone;
    const userName = this.userContext?.currentUserUserName;

    const userInformation: UserInformation = {
      firstName: firstName,
      lastName: lastName,
      email: email,
      phoneNumber: phoneNumber,
      userName: userName
    };

    const groupInformation: GroupInformation = {
      id: this.policyContext?.groupId,
      name: this.policyContext?.policyHolder,
      number: groupNumber
    };

    const lineItems: LineItemInfo[] = paymentDataArray.map((paymentData) => {
      const invoiceInfo = {
        period: paymentData.InvoicePeriod,
        number: paymentData.InvoiceNumber,
        amount: paymentData.Amount
      } as InvoiceInfo;

      const billingGroupInfo = {
        name: paymentData.BillGroupName,
        number: paymentData.BillGroupNumber
      } as BillingGroupInfo;

      const lineItemInfo = {
        invoice: invoiceInfo,
        billingGroup: billingGroupInfo
      } as LineItemInfo;

      return lineItemInfo;
    });

    const paymentInformation = this.paymentInformationGet(formGroup);

    const paymentRequest = {
      amountDetails: amountDetails,
      groupInformation: groupInformation,
      lineItems: lineItems,
      paymentInformation: paymentInformation,
      userInformation: userInformation
    } as PaymentRequest;

    return paymentRequest;
  }

  goBillingList() {
    this.parent.goBillingList();
  }

  modalClose(type: ModalType) {
    if (type === ModalType.Success || type === ModalType.Permission) {
      this.modalShow = false;
      this.transferToBilling();
    } else {
      this.modalShow = false;
      this.modalDataClear();
    }
  }

  modalOpen(type: ModalType) {
    this.modalShow = true;

    switch (type) {
      case ModalType.Error4xx:
        this.modalData.title = this.ModalTitleError4xx;
        this.modalData.body = this.ModalBodyError4xx;
        break;
      case ModalType.Error5xx:
        this.modalData.title = this.ModalTitleError5xx;
        this.modalData.body = this.ModalBodyError5xx;
        break;
      case ModalType.Legal:
        this.modalData.title = this.ModalTitleLegal;
        this.modalData.body = this.ModalBodyLegal;
        break;
      case ModalType.Permission:
        this.modalData.title = this.ModalTitlePermission;
        this.modalData.body = this.ModalBodyPermission;
        break;
      case ModalType.Success:
        this.modalData.title = this.ModalTitleSuccess;
        this.modalData.body = this.ModalBodySuccess;
        this.hasSubText = true;
        this.modalData.subText = this.ModalBodyEmail;
        break;

      default:
        break;
    }

    this.modalData.type = type;
    this.isLoading = false;
  }

  ngOnInit(): void {
    this.formGroup.get(this.AccountTypeSelectionKey)?.setValue(AccountType.Checking);
    this.hasSubmitPermission = this.hasPermissions(this.userContext);
  }

  onAmountChange(paymentChangeObj: IPaymentChange) {
    const amountUiError = !this.paymentFieldValid(paymentChangeObj.item);

    // :  Update presentation item
    paymentChangeObj.item.AmountUiError = amountUiError;

    const paymentDataNew = {
      ...paymentChangeObj.item,
      AmountUiError: amountUiError
    };

    this.paymentDataArray = this.paymentDataArrayUpdate(this.paymentDataArray, paymentDataNew);

    if (!amountUiError) {
      this.totalAmount = this.totalAmountCalculate(this.paymentDataArray);
    }
  }

  onClickTableHeader(sortField: string) {
    let newSortDirection = SearchSortDirection.Descending;

    if (this.searchParameter.sortDirection === SearchSortDirection.Descending) {
      newSortDirection = SearchSortDirection.Ascending;
    }

    const field: SearchSortField = SearchSortField[sortField as keyof typeof SearchSortField];
    this.sortDirection$.next(newSortDirection);
    this.sortField$.next(field);
  }

  onPageChange(page: number) {
    this.page$.next(page);
    this.parent.scrollToTop();
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize$.next(pageSize);
  }

  pageValid() {
    const paymentInformation = this.paymentInformationGet(this.formGroup);
    const pageValid = this.pageValidLocal(this.paymentDataArray, paymentInformation);
    return pageValid;
  }

  payNow() {
    this.modalShow = true;
    this.isLoading = true;

    if (this.hasSubmitPermission) {
      if (this.formGroup.invalid) {
        this.controlsUpdate(this.formGroup);
        return;
      }

      // :  Asynchronous retrieval should have completed by the time this method is called
      // const groupNumber = this.groupNumberGet(this.policyContext?.groupId ?? Constants.StringEmpty, this.tupleArray);
      const groupNumber = this.policyContext?.groupNumber ?? Constants.StringEmpty;

      this.paymentRequestSend(groupNumber, this.paymentDataArray);
    } else {
      this.modalOpen(ModalType.Permission);
    }
  }

  async submit() {
    console.log('Submitted');
  }

  private amountUpdate(item: PaymentData): PaymentData {
    const amountString = item.AmountUi;
    const amountNew = this.currencyParse(amountString);
    const amountUiNew = item.AmountUi ?? this.currencyStringCreate(amountNew);

    const itemNew = {
      ...item,
      Amount: amountNew,
      AmountUi: amountUiNew
    };

    return itemNew;
  }

  private controlsUpdate(control: AbstractControl) {
    if (control instanceof UntypedFormControl) {
      control.updateValueAndValidity();
    } else if (control instanceof UntypedFormGroup) {
      Object.values(control.controls).forEach((childControl) => this.controlsUpdate(childControl));
    } else if (control instanceof UntypedFormArray) {
      control.updateValueAndValidity();
      control.controls.forEach((childControl) => this.controlsUpdate(childControl));
    }
  }

  private currencyParse(value: string): number {
    const amountString = value.replace(/[^0-9.]/g, Constants.StringEmpty);
    const amountNumber = Number(amountString);
    return amountNumber;
  }

  private currencyStringCreate(value: number): string {
    const currencyPipe = new CurrencyPipe(Constants.LocaleEnglishUs, Constants.CurrencyUsdKey);
    const amountUIMaybe = currencyPipe.transform(value);
    const fallbackValue = Constants.StringEmpty;
    const currencyString = amountUIMaybe ? amountUIMaybe : fallbackValue;

    return currencyString;
  }

  private groupNumberGet(groupId: string, tupleArray: [employerId: string, groupId: string][]): string {
    const groupNumberArray = tupleArray.filter((tuple) => tuple[0] === groupId).map((tuple) => tuple[1]);
    const groupNumber = groupNumberArray[0];

    return groupNumber;
  }

  private groupTupleArrayRetrieveAsync(tupleArrayIn: [employerId: string, groupId: string][]) {
    const policySearchRequest = {
      category: SearchCategory.GroupId,
      criteria: '2',
      page: 1,
      pageSize: 10,
      sortField: SearchSortField.Name,
      sortDirection: SearchSortDirection.Ascending
    };

    const observableTupleArray = this.policyClient
      .search(policySearchRequest)
      .pipe(map((x) => this.policySearchResponseTransform(x)));

    observableTupleArray.subscribe((tupleArray) => {
      tupleArray.forEach((tuple) => {
        tupleArrayIn.push(tuple);
      });
    });
  }

  private hasPermissions(input: UserContext) {
    const formattedSymClass = input?.symClass?.toLowerCase();
    const isPermittedPH = formattedSymClass === 'sponsor' && input.policyholderInfo?.isAdmin === true;
    if (formattedSymClass === 'distributor' || isPermittedPH) {
      return true;
    } else {
      return false;
    }
  }

  private itemNewCreate(item?: PaymentData, elementDeleted?: PaymentData): PaymentData {
    const itemNew: PaymentData = item
      ? this.amountUpdate(item)
      : elementDeleted
        ? elementDeleted
        : this.paymentDataInit();

    return itemNew;
  }

  private modalDataClear() {
    this.modalData = {
      title: Constants.StringEmpty,
      body: Constants.StringEmpty,
      subText: Constants.StringEmpty,
      type: ModalType.Success
    };
  }

  private pageValidLocal(paymentDataArray: PaymentData[], paymentInformation: PaymentInformation): boolean {
    const paymentDataValidArray = paymentDataArray.map((paymentData) => {
      const paymentFieldValid = this.paymentFieldValid(paymentData);
      return paymentFieldValid;
    });

    const paymentInfoValid = this.paymentInformationValid(paymentInformation);

    const pageValid = !paymentDataValidArray.includes(false) && paymentInfoValid;

    return pageValid;
  }

  private paymentDataArrayUpdate(paymentDataArray: PaymentData[], item?: PaymentData) {
    const index = paymentDataArray.findIndex((x) => item && x.InvoiceNumber === item.InvoiceNumber);

    const elementDeleted: PaymentData | undefined = index >= 0 ? paymentDataArray.splice(index, 1).at(0) : undefined;

    const itemNew: PaymentData = this.itemNewCreate(item, elementDeleted);

    paymentDataArray.push(itemNew);

    return paymentDataArray;
  }

  private paymentDataInit(): PaymentData {
    const paymentData = {
      Amount: 0,
      AmountUi: Constants.StringEmpty,
      AmountUiError: false,
      BillGroupName: Constants.StringEmpty,
      BillGroupNumber: Constants.StringEmpty,
      Invoice: Constants.StringEmpty,
      InvoiceNumber: Constants.StringEmpty,
      InvoicePeriod: Defaults.spanInfoEnhancedDefault
    } as PaymentData;

    return paymentData;
  }

  private paymentFieldValid(item: PaymentData): boolean {
    const fieldValue = item.AmountUi;
    const decimalPointCount = fieldValue.split('.').length - 1;
    const dollarSignCount = fieldValue.split('$').length - 1;
    const valueParsed = this.currencyParse(fieldValue);

    const fieldValid = decimalPointCount <= 1 && dollarSignCount <= 1 && valueParsed > 0;

    return fieldValid;
  }

  private paymentInformationGet(formGroup: UntypedFormGroup): PaymentInformation {
    const paymentInformation: PaymentInformation = {
      accountType: formGroup.get(this.AccountTypeSelectionKey)?.value,
      accountNumber: formGroup.get(this.AccountNumberKey)?.value,
      routingNumber: formGroup.get(this.RoutingNumberKey)?.value
    };

    return paymentInformation;
  }

  private paymentInformationValid(paymentInformation: PaymentInformation): boolean {
    const accountTypeValid = paymentInformation.accountType !== Constants.StringEmpty;

    const accountNumberValid =
      paymentInformation.accountNumber !== null &&
      paymentInformation.accountNumber !== undefined &&
      paymentInformation.accountNumber.length >= AccountFieldLengthMin;

    const routingNumberValid =
      paymentInformation.routingNumber !== null &&
      paymentInformation.routingNumber !== undefined &&
      paymentInformation.routingNumber.length == RoutingFieldLength;

    return accountTypeValid && accountNumberValid && routingNumberValid;
  }

  private paymentRequestSend(groupNumber: string, paymentDataArray: PaymentData[]) {
    const paymentRequest = this.createEndpointPaymentRequest(this.formGroup, groupNumber, paymentDataArray);

    // :  Prevent the centralized error handling in 'http-error.interceptor.ts' from redirecting to the "error page"
    //    (or to the dashboard if a centralized error page is not defined)
    this.errorService.preventNextErrorRedirect();

    this.paymentClient
      .bank(paymentRequest)
      .pipe(
        catchError((httpErrorResponse: HttpErrorResponse) => {
          return throwError(() => httpErrorResponse);
        })
      )
      .subscribe({
        complete: () => {
          // :  Success condition
          this.modalOpen(ModalType.Success);
        },
        error: (httpErrorResponse: HttpErrorResponse) => {
          // :  Error condition of some sort
          if (httpErrorResponse.status >= 500) {
            this.modalOpen(ModalType.Error5xx);
          } else {
            this.modalOpen(ModalType.Error4xx);
          }
        }
      });
  }

  private policySearchResponseTransform(
    apiResponse: ApiResponseOfPolicySearchResultsResponse
  ): [employerId: string, groupId: string][] {
    const tupleArray = (apiResponse.data?.searchResults ?? [])
      .map((result) => {
        let tuple: [employerId: string, groupId: string] | undefined = undefined;

        if (result.employerId !== undefined && result.groupId !== undefined) {
          tuple = [result.employerId, result.groupId];
        }

        return tuple;
      })
      .filter((tuple) => tuple !== undefined);

    const tupleArrayFiltered = tupleArray as [string, string][];

    return tupleArrayFiltered;
  }

  private totalAmountCalculate(paymentDataArray: PaymentData[]): number {
    const total: number = paymentDataArray.reduce((accum, current) => {
      const amount = Number(current.Amount);
      const accumNew = accum + amount;
      return accumNew;
    }, 0.0);

    return total;
  }

  private transferToBilling() {
    this.billingService.setComponent(Constants.ListKey);
    this.billingService.clearSelected();
    this.parent.payNow();
  }
}
